import { createAsyncThunk } from '@reduxjs/toolkit';
import { auth, db, getAuthInstance } from '../../firebase';
import { Toast } from '../../components/Common/Toast';
import { cleanObject, encryptToken } from '../../helper/helper';
import { callAPI } from '../../services/api.service';
import { TokenCookieManager } from '../../utils/tokenCookies';
import { readAuthToken, writeAuthToken } from '../../utils/authToken';
import { clearImpersonationSession, shouldUseImpersonationAuth } from '../../utils/impersonation.utils';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, signInWithCustomToken, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  setRedirectURL,
  setStudentTokenAuth,
  setUnsubscribeUserSnapShot,
  setUser,
  setUserLoader,
  setUserUpdateSubscriptionAndUnsubscribe,
} from '../userSlice';
import { UserLocalStorageKeysEnum, UserModel, UserRoleEnum, UserStatusEnum } from '../../interfaces/user.interface';
import { CONFIG } from '../../config/mainConfig';
import { AppDispatch, RootState } from '../store';
import { NavigateFunction } from 'react-router-dom';
import { handleUnload } from '../../routes/UserRouteGuard';
import { HTTPStatusEnum } from '../../interfaces/log.interface';
import { setArtStyles } from '../appSlice';
import { LocationType } from '../../pages/Auth/LocationSelection';
import { ParentPermissionForm } from '../../pages/Auth/ParentPermission';
import { getTeacherRedirectURL } from '../../pages/Teacher/utils/onboarding.utils';

export const fetchUserDetails = createAsyncThunk(
  'user/fetchUserDetails',
  async (
    data: { email: string; validate?: (currentUser: UserModel) => { isValid: boolean; message?: string } },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const currentUser = await callAPI(`user/getUser?email=${data.email}`);
      if (currentUser?.status === HTTPStatusEnum.FORBIDDEN) {
        Toast('You do not have an account, please sign up', true);
        return null;
      } else if (currentUser?.status === UserStatusEnum.ACTIVE) {
        if (data.validate) {
          const validation = data.validate(currentUser);
          if (!validation.isValid) {
            Toast(validation.message || 'Validation failed', true);
            return null;
          }
        }
        // Token Management
        const currentAuth = getAuthInstance();
        const idTokenResult = await currentAuth.currentUser?.getIdTokenResult();
        if (idTokenResult) {
          const encryptedToken = encryptToken(idTokenResult.token, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || '');
          if (encryptedToken !== readAuthToken()) {
            writeAuthToken(encryptedToken);
          }
        }

        // Update training popup if not available
        if (!currentUser?.showTrainingPopup) {
          await callAPI('user/updateUser', {
            email: currentUser.email,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            settings: currentUser.settings,
          });
        }

        localStorage.setItem(UserLocalStorageKeysEnum.CURRENT_USER, JSON.stringify(currentUser));

        return {
          ...currentUser,
          email: data.email,
        };
      } else {
        Toast('Account Not Active!', true);
        TokenCookieManager.removeToken();
        localStorage.removeItem(UserLocalStorageKeysEnum.EDIT_MODE);
        localStorage.removeItem(UserLocalStorageKeysEnum.CURRENT_USER);
        return null;
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const autoFetchUserDetails = createAsyncThunk(
  'user/autoFetchUserDetails',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      return new Promise<void>((resolve, reject) => {
        const state: RootState = getState() as RootState;
        if (state.user.userUpdateSubscriptionAndUnsubscribe) {
          resolve();
          return;
        }
        const currentAuth = getAuthInstance();
        const unsubscribeAuth = onAuthStateChanged(currentAuth, async (currentUser: any) => {
          const state: RootState = getState() as RootState;
          if (!currentUser) {
            unsubscribeAuth();
            state.user.userUpdateSubscriptionAndUnsubscribe && state.user.userUpdateSubscriptionAndUnsubscribe();
            state.user.unsubscribeUserSnapShot && state.user.unsubscribeUserSnapShot();
            dispatch(setUserUpdateSubscriptionAndUnsubscribe(null));
            dispatch(setUnsubscribeUserSnapShot(null));
            dispatch(setUser(null));
            reject('User not authenticated');
            return;
          }
          dispatch(setUserUpdateSubscriptionAndUnsubscribe(unsubscribeAuth));
          const { stopLoadUpdates } = state.user;
          if (currentUser && !stopLoadUpdates) {
            // Fetch user details and listen for updates
            const userDocRef = doc(db, 'users', currentUser.email);
            const unsubscribeUserSnapShot = onSnapshot(userDocRef, async (docSnap) => {
              if (docSnap.exists()) {
                const user = await callAPI(`user/getUser?email=${currentUser.email}`);
                dispatch(setUser(user));
                localStorage.setItem(UserLocalStorageKeysEnum.CURRENT_USER, JSON.stringify(user || '{}'));
                dispatch(setUserLoader(false));

                // Token management logic
                const idTokenResult = await currentUser.getIdTokenResult();
                let encryptedToken = encryptToken(idTokenResult.token, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || '');

                if (
                  encryptedToken !== readAuthToken() ||
                  Date.parse(idTokenResult.expirationTime) < Date.now()
                ) {
                  try {
                    // Refresh and store new token if expired or missing
                    await currentUser.getIdToken(true);
                    encryptedToken = encryptToken(idTokenResult?.token, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || '');
                    writeAuthToken(encryptedToken);
                  } catch (err) {}
                }
              } else {
                dispatch(setUserLoader(false));
                reject('User document not found');
              }
            });

            dispatch(setUnsubscribeUserSnapShot(unsubscribeUserSnapShot));

            // Cleanup function for snapshot listener
            return () => {
              const state: RootState = getState() as RootState;
              unsubscribeUserSnapShot();
              state.user.unsubscribeUserSnapShot && state.user.unsubscribeUserSnapShot();
            };
          } else {
            dispatch(setUserLoader(false));
            reject('User not authenticated');
          }
        });

        // Cleanup function for auth state listener
        return () => {
          const state: RootState = getState() as RootState;
          unsubscribeAuth();
          state.user.userUpdateSubscriptionAndUnsubscribe && state.user.userUpdateSubscriptionAndUnsubscribe();
        };
      });
    } catch (error: any) {
      dispatch(setUserLoader(false));
      return rejectWithValue(error.message);
    }
  },
);

export const forceFetchUserDetails = createAsyncThunk(
  'user/forceFetchUserDetails',
  async (callback: (user: UserModel) => void, { rejectWithValue, dispatch, getState }) => {
    try {
      return new Promise<void>((resolve, reject) => {
        const currentAuth = getAuthInstance();
        const unsubscribeAuth = onAuthStateChanged(currentAuth, async (currentUser: any) => {
          const state: RootState = getState() as RootState;
          if (currentUser) {
            // Fetch user details and listen for updates
            const userDocRef = doc(db, 'users', currentUser.email);
            const unsubscribeUserSnapShot = onSnapshot(userDocRef, async (docSnap) => {
              if (docSnap.exists()) {
                const user = await callAPI(`user/getUser?email=${currentUser.email}`);
                localStorage.setItem(UserLocalStorageKeysEnum.CURRENT_USER, JSON.stringify(user || '{}'));

                // Token management logic
                const idTokenResult = await currentUser.getIdTokenResult();
                let encryptedToken = encryptToken(idTokenResult.token, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || '');

                if (
                  encryptedToken !== readAuthToken() ||
                  Date.parse(idTokenResult.expirationTime) < Date.now()
                ) {
                  // Refresh and store new token if expired or missing
                  await currentUser.getIdToken(true);
                  encryptedToken = encryptToken(idTokenResult?.token, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || '');
                  writeAuthToken(encryptedToken);
                }
                callback && callback(user);
              } else {
                dispatch(setUserLoader(false));
                reject('User document not found');
              }
              unsubscribeUserSnapShot();
              unsubscribeAuth();
            });

            // Cleanup function for snapshot listener
            return () => unsubscribeUserSnapShot();
          }
        });

        // Cleanup function for auth state listener
        return () => unsubscribeAuth();
      });
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Sign in user
export const signInUser = createAsyncThunk(
  'user/signInUser',
  async (
    values: { email: string; password: string; dispatch: AppDispatch; navigateTo?: NavigateFunction },
    { rejectWithValue },
  ) => {
    try {
      let { email, password, dispatch, navigateTo } = values;

      email = email?.toLowerCase()?.trim();

      if (!email) {
        Toast('Email is required', true);
        return rejectWithValue('Email is required');
      }

      // Check if the user is verified
      const data = await callAPI(`user/check-user-verified?email=${email}`);
      if (data?.status === HTTPStatusEnum.FORBIDDEN) {
        Toast('You do not have an account, please sign up', true);
        return;
      }

      if (!data?.verified) {
        Toast('User Not Verified', true);
        return rejectWithValue('User Not Verified');
      } else if (data?.status !== UserStatusEnum.ACTIVE) {
        Toast('User Not Active', true);
        return rejectWithValue('User Not Active');
      }

      // Sign in the user with Firebase
      const userCredential: any = await signInWithEmailAndPassword(auth, email, password);

      dispatch(
        forceFetchUserDetails((user) => {
          if (!user) {
            return;
          }
          let redirectURL = '/';
          if (user?.role === UserRoleEnum.teacher) {
            redirectURL = getTeacherRedirectURL(user) || '/classroom/dashboard';
          }
          dispatch(setUser(user));
          dispatch(autoFetchUserDetails());
          values.navigateTo && dispatch(setRedirectURL(redirectURL));
        }),
      );

      // Encrypt the token
      const encryptedToken = encryptToken(userCredential?.user?.accessToken, process.env.REACT_APP_TOKEN_ENCRYPT_KEY!);

      // Store the token in cookies
      TokenCookieManager.setToken(encryptedToken);

      callAPI(`user/auth/track`, { type: 'email' }, 'POST')
        .then()
        .catch(() => {});

      Toast('Login Successfully');
      return {
        email,
        token: encryptedToken,
      };
    } catch (error: any) {
      Toast('Fail to Login', true);
      return rejectWithValue(error.message || 'Unknown error occurred');
    }
  },
);

// Sign in student
export const signInStudent = createAsyncThunk(
  'user/signInStudent',
  async (
    values: { username: string; password: string; location?: LocationType | null; navigateTo: NavigateFunction },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      let { username, password, location, navigateTo } = values;
      const state: RootState = getState() as RootState;

      username = username?.toLowerCase()?.trim();

      if (!location) {
        location = state.user?.studentLocationAuth;
      }

      if (!location) {
        navigateTo('/auth/location');
        return;
      }

      if (!username) {
        Toast('Username is required', true);
        return rejectWithValue('Username is required');
      }

      // Check if the user is verified
      const data = await callAPI(`user/check-user-verified?username=${username}`);
      if (data?.status === HTTPStatusEnum.FORBIDDEN) {
        Toast('You do not have an account, please sign up', true);
        return;
      }

      if (!data?.verified) {
        Toast('User Not Verified', true);
        return rejectWithValue('User Not Verified');
      } else if (data?.status !== UserStatusEnum.ACTIVE) {
        Toast('User Not Active', true);
        return rejectWithValue('User Not Active');
      }

      const request = await callAPI(`api/auth/signInStudent`, {
        username,
        password,
        location,
      });

      if (request?.status !== HTTPStatusEnum.OK) {
        throw new Error('Failed to Login');
      }
      dispatch(setStudentTokenAuth(request.token));
      if (request.accepted) {
        const userCredential: any = await signInWithCustomToken(auth, request.token);
        // Encrypt the token
        const encryptedToken = encryptToken(
          userCredential?.user?.accessToken,
          process.env.REACT_APP_TOKEN_ENCRYPT_KEY!,
        );

        // Store the token in cookies
        TokenCookieManager.setToken(encryptedToken);
        dispatch(setStudentTokenAuth(null));
        dispatch(
          forceFetchUserDetails((user) => {
            if (!user) {
              return;
            }
            dispatch(setUser(user));
            dispatch(autoFetchUserDetails());
            setTimeout(() => dispatch(setRedirectURL('/')), 250);
          }),
        );
        return;
      } else {
        dispatch(setStudentTokenAuth(request.token));
        navigateTo('/auth/student/growup');
      }
    } catch (error: any) {
      Toast('Fail to Login', true);
      return rejectWithValue(error.message || 'Unknown error occurred');
    }
  },
);

// Sign in student
export const signInStudentWithConfirmation = createAsyncThunk(
  'user/signInStudentWithConfirmation',
  async (
    values: {
      formData: ParentPermissionForm;
      navigateTo: NavigateFunction;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const { formData, navigateTo } = values;
      let { firstName, lastName, email, phone, isGuardian, confirmShare, marketing } = formData;

      const state: RootState = getState() as RootState;
      if (!state.user?.studentTokenAuth) {
        navigateTo('/auth/student');
      }
      await signInWithCustomToken(auth, state.user?.studentTokenAuth!);
      const idToken = await auth.currentUser?.getIdToken();

      const request = await callAPI(
        `api/auth/parent-confirmation`,
        cleanObject({
          firstName,
          lastName,
          email,
          phone,
          isGuardian,
          confirmShare,
          marketing,
          token: idToken,
        }),
      );

      if (request?.status !== HTTPStatusEnum.OK || !request.accepted) {
        await signOut(auth);
        throw new Error('Failed to confirm authorization');
      }
      const encryptedToken = encryptToken(idToken!, process.env.REACT_APP_TOKEN_ENCRYPT_KEY!);

      // Store the token in cookies
      TokenCookieManager.setToken(encryptedToken);
      dispatch(setStudentTokenAuth(null));
      dispatch(
        forceFetchUserDetails((user) => {
          if (!user) {
            return;
          }
          dispatch(setUser(user));
          dispatch(autoFetchUserDetails());
          setTimeout(() => dispatch(setRedirectURL('/')), 250);
        }),
      );
    } catch (error: any) {
      Toast('Fail to Login', true);
      return rejectWithValue(error.message || 'Unknown error occurred');
    }
  },
);

// Async Thunk for fetching training videos
export const fetchTrainingVideos = createAsyncThunk('user/fetchTrainingVideos', async (_, { rejectWithValue }) => {
  try {
    const data = await callAPI('training-videos');
    return data?.links || {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

// Async Thunk for fetching training videos
export const fetchAllArtStyles = createAsyncThunk(
  'user/fetchAllArtStyles',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const data = await callAPI('artstyle/all');
      const list = data?.artStyle || [];
      dispatch(setArtStyles(list));
      return list;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async Thunk for user logout
export const logOut = createAsyncThunk(
  'user/logout',
  async (
    data: { user?: UserModel | null; navigateTo?: NavigateFunction; callback?: () => void },
    { rejectWithValue, dispatch },
  ) => {
    try {
      // In an impersonation tab, sign out of the tab-scoped impersonation auth
      // and clear the session token from sessionStorage. In a normal session,
      // sign out the main auth and clear the cookie.
      if (shouldUseImpersonationAuth()) {
        const { signOutImpersonationUser } = await import('../../firebase/impersonationAuth');
        await signOutImpersonationUser();
        clearImpersonationSession();
      } else {
        await signOut(auth);
        TokenCookieManager.removeToken();
      }

      dispatch(setUser(null));
      window.removeEventListener('beforeunload', handleUnload);

      localStorage.removeItem(UserLocalStorageKeysEnum.CURRENT_USER);
      localStorage.removeItem(UserLocalStorageKeysEnum.BOOK_ID);
      localStorage.removeItem(UserLocalStorageKeysEnum.EDIT_MODE);
      localStorage.removeItem(UserLocalStorageKeysEnum.STORY_STEP);
      localStorage.removeItem(CONFIG.PROMO_PLAN);

      // dispatch(setCurrentStep(0));
      // dispatch(setDraftId(null));
      // dispatch(setAppKey(new Date().getTime()));
      // dispatch({ type: 'RESET_APP' });
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          data.callback && data.callback();
          const mainURLPath = data?.user?.role === UserRoleEnum.student ? '/auth/student' : '/';
          if (data.navigateTo) {
            data.navigateTo(`${mainURLPath}?${CONFIG.BROWSER_REFRESH_POPUP_PARAM}=no`);
          } else {
            window.location.href = `/#${mainURLPath}?${CONFIG.BROWSER_REFRESH_POPUP_PARAM}=no`;
          }
          setTimeout(() => window.location.reload(), 250);
          resolve();
        }, 100);
      });
    } catch (error) {
      Toast('Failed to log out. Please try again.', true);
    }
  },
);

// Async Thunk for unsubscribing user subscriptions
export const unsubscribeUserSubscriptions = createAsyncThunk(
  'user/unsubscribeUserSubscriptions',
  async (_, { getState, dispatch }) => {
    try {
      await signOut(auth);
      const state: RootState = getState() as RootState;
      state.user.userUpdateSubscriptionAndUnsubscribe && state.user.userUpdateSubscriptionAndUnsubscribe();
      state.user.unsubscribeUserSnapShot && state.user.unsubscribeUserSnapShot();
      dispatch(setUserUpdateSubscriptionAndUnsubscribe(null));
      dispatch(setUnsubscribeUserSnapShot(null));
    } catch (error) {}
  },
);
